import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  FileImage,
  ScanLine,
  Send,
  ShieldCheck,
  UploadCloud,
  XCircle,
} from "lucide-react";
import {
  adminAnalyzeChart,
  adminUploadChart,
  cancelAdminTradeRecommendation,
  forwardAdminChartAnalysis,
  listAdminChartAnalyses,
  listAdminTradeRecommendations,
} from "../lib_api";

const REQUIRED_TFS = ["H4", "H1", "M30", "M5"];
const CONTROL_CLASS =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 shadow-sm outline-none transition placeholder:text-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";
const LABEL_CLASS =
  "mb-1.5 block text-xs font-semibold uppercase text-slate-600 dark:text-slate-300";
const PANEL_CLASS =
  "overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900";

function resolveAnalysisId(uploaded: any): number | null {
  const rawId =
    uploaded?.id ??
    uploaded?.analysisId ??
    uploaded?.requestId ??
    uploaded?.chartAnalysisId ??
    uploaded?.original_chart_analysis_id;
  const parsed = Number(rawId || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export default function AdminSignals() {
  const qc = useQueryClient();
  const [symbol, setSymbol] = useState("XAUUSD");
  const [strategyContext, setStrategyContext] = useState("");
  const [riskNote, setRiskNote] = useState("");
  const [expiryMinutes, setExpiryMinutes] = useState(20);
  const [forwardMode, setForwardMode] = useState<
    "one_user" | "selected_users" | "all_live_users"
  >("all_live_users");
  const [targetUserId, setTargetUserId] = useState("");
  const [targetUserIds, setTargetUserIds] = useState("");
  const [proOnly, setProOnly] = useState(true);
  const [items, setItems] = useState<
    { file: File; timeframe: string; id: string }[]
  >([]);
  const [review, setReview] = useState<any>(null);
  const [uploadedAnalysisId, setUploadedAnalysisId] = useState<number | null>(
    null,
  );

  const analyses = useQuery({
    queryKey: ["admin-chart-analyses"],
    queryFn: listAdminChartAnalyses,
  });
  const recommendations = useQuery({
    queryKey: ["admin-trade-recommendations"],
    queryFn: listAdminTradeRecommendations,
    refetchInterval: 10000,
  });

  const upload = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.append("symbol", symbol);
      form.append(
        "timeframes",
        JSON.stringify(items.map((it) => it.timeframe)),
      );
      items.forEach((it) => form.append("screenshots", it.file));
      return adminUploadChart(form);
    },
    onSuccess: (uploaded: any) => {
      const analysisId = resolveAnalysisId(uploaded);
      if (!analysisId) {
        setUploadedAnalysisId(null);
        toast.error(
          "Chart upload request was not created. Please upload chart first.",
        );
        return;
      }
      setUploadedAnalysisId(analysisId);
      toast.success(`Chart upload request created (#${analysisId}).`);
      qc.invalidateQueries({ queryKey: ["admin-chart-analyses"] });
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.error || error?.message || "Chart upload failed",
      ),
  });

  const analyze = useMutation({
    mutationFn: async () => {
      if (!uploadedAnalysisId) {
        throw new Error(
          "Chart upload request was not created. Please upload chart first.",
        );
      }
      return adminAnalyzeChart(uploadedAnalysisId, {
        strategyContext,
        riskNote,
        expiryMinutes,
      });
    },
    onSuccess: (data) => {
      setReview(data);
      toast.success("Chart analyzed. Review before forwarding.");
      qc.invalidateQueries({ queryKey: ["admin-chart-analyses"] });
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.error || error?.message || "Analysis failed",
      ),
  });

  const forward = useMutation({
    mutationFn: () =>
      forwardAdminChartAnalysis(Number(review?.id), {
        mode: forwardMode,
        userId: targetUserId ? Number(targetUserId) : null,
        userIds: targetUserIds
          .split(",")
          .map((v) => Number(v.trim()))
          .filter(Boolean),
        proOnly,
        strategyContext,
        riskNote,
        expiryMinutes,
      }),
    onSuccess: (resp: any) => {
      toast.success(`Forwarded to ${resp?.recipients?.length || 0} users.`);
      qc.invalidateQueries({ queryKey: ["admin-trade-recommendations"] });
    },
    onError: (error: any) =>
      toast.error(
        error?.response?.data?.error || error?.message || "Forwarding failed",
      ),
  });

  const cancel = useMutation({
    mutationFn: (id: number) => cancelAdminTradeRecommendation(id),
    onSuccess: () => {
      toast.info("Recommendation cancelled.");
      qc.invalidateQueries({ queryKey: ["admin-trade-recommendations"] });
    },
  });

  const canAnalyze = useMemo(() => {
    if (items.length !== REQUIRED_TFS.length) return false;
    const selected = new Set(items.map((item) => item.timeframe));
    return REQUIRED_TFS.every((tf) => selected.has(tf));
  }, [items]);

  const selectedTimeframes = new Set(items.map((item) => item.timeframe));
  const missingTimeframes = REQUIRED_TFS.filter(
    (timeframe) => !selectedTimeframes.has(timeframe),
  );

  return (
    <main className="space-y-4 text-slate-900 dark:text-slate-100">
      <section className={PANEL_CLASS}>
        <header className="border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-950/70">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase text-sky-700 dark:text-sky-300">
                Admin signal workspace
              </p>
              <h1 className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">
                Multi-timeframe chart analysis
              </h1>
              <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
                Validate one market across H4, H1, M30 and M5 before forwarding
                a setup.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
              <ShieldCheck
                aria-hidden="true"
                className="h-4 w-4 text-emerald-600"
              />
              EMA 9/21 · BB 20/2 · RSI 14 · MACD 12/26/9
            </div>
          </div>
        </header>

        <div className="space-y-5 p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <label>
              <span className={LABEL_CLASS}>Market symbol</span>
              <input
                className={CONTROL_CLASS}
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="e.g. XAUUSD"
              />
            </label>
            <label>
              <span className={LABEL_CLASS}>Strategy context</span>
              <input
                className={CONTROL_CLASS}
                value={strategyContext}
                onChange={(e) => setStrategyContext(e.target.value)}
                placeholder="Describe the setup or market structure"
              />
            </label>
            <label>
              <span className={LABEL_CLASS}>Risk note</span>
              <input
                className={CONTROL_CLASS}
                value={riskNote}
                onChange={(e) => setRiskNote(e.target.value)}
                placeholder="Add execution or event-risk guidance"
              />
            </label>
            <label>
              <span className={LABEL_CLASS}>Signal expiry</span>
              <div className="relative">
                <Clock3
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-500"
                />
                <input
                  className={`${CONTROL_CLASS} pl-9 pr-16`}
                  type="number"
                  min={5}
                  max={120}
                  value={expiryMinutes}
                  onChange={(e) =>
                    setExpiryMinutes(Number(e.target.value || 20))
                  }
                  aria-label="Signal expiry in minutes"
                />
                <span className="pointer-events-none absolute right-3 top-3 text-xs font-semibold text-slate-500">
                  MIN
                </span>
              </div>
            </label>
          </div>

          <div className="border-t border-slate-200 pt-5 dark:border-slate-700">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold text-slate-950 dark:text-white">
                  Chart screenshots
                </h2>
                <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                  Select exactly four PNG, JPEG or WebP files, then verify each
                  timeframe.
                </p>
              </div>
              <span
                className={`text-xs font-semibold ${items.length === 4 ? "text-emerald-600 dark:text-emerald-400" : "text-slate-600 dark:text-slate-300"}`}
              >
                {items.length}/4 selected
              </span>
            </div>
            <label className="block">
              <span className="sr-only">Choose chart screenshots</span>
              <input
                className={`${CONTROL_CLASS} cursor-pointer p-1.5 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-700`}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  setUploadedAnalysisId(null);
                  setItems(
                    Array.from(e.target.files || [])
                      .slice(0, REQUIRED_TFS.length)
                      .map((f, idx) => ({
                        file: f,
                        timeframe: REQUIRED_TFS[idx],
                        id: `${f.name}-${idx}-${f.size}`,
                      })),
                  );
                }}
              />
            </label>

            {items.length ? (
              <div className="mt-4 divide-y divide-slate-200 border-y border-slate-200 dark:divide-slate-700 dark:border-slate-700">
                {items.map((it, idx) => (
                  <div
                    key={it.id}
                    className="grid items-center gap-3 py-3 sm:grid-cols-[minmax(0,1fr)_10rem]"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <FileImage
                        aria-hidden="true"
                        className="h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                          {it.file.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {Math.max(1, Math.round(it.file.size / 1024))} KB
                        </p>
                      </div>
                    </div>
                    <label>
                      <span className="sr-only">
                        Timeframe for {it.file.name}
                      </span>
                      <select
                        className={CONTROL_CLASS}
                        value={it.timeframe}
                        onChange={(e) =>
                          setItems((prev) =>
                            prev.map((x, i) =>
                              i === idx
                                ? { ...x, timeframe: e.target.value }
                                : x,
                            ),
                          )
                        }
                      >
                        {REQUIRED_TFS.map((tf) => (
                          <option key={tf} value={tf}>
                            {tf}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 dark:bg-sky-600 dark:hover:bg-sky-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
                disabled={!canAnalyze || upload.isPending}
                onClick={() => upload.mutate()}
              >
                <UploadCloud aria-hidden="true" className="h-4 w-4" />
                {upload.isPending
                  ? "Uploading charts..."
                  : "Create upload request"}
              </button>
              <button
                className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
                disabled={
                  !canAnalyze || !uploadedAnalysisId || analyze.isPending
                }
                onClick={() => analyze.mutate()}
              >
                <ScanLine aria-hidden="true" className="h-4 w-4" />
                {analyze.isPending ? "Analyzing charts..." : "Analyze chart"}
              </button>
            </div>

            {uploadedAnalysisId ? (
              <div className="mt-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                <CheckCircle2
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0"
                />
                <span>
                  Upload request #{uploadedAnalysisId} is ready for analysis.
                </span>
              </div>
            ) : (
              <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                <AlertTriangle
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0"
                />
                <span>
                  {items.length === 0
                    ? "Choose four chart screenshots to begin."
                    : missingTimeframes.length
                      ? `Assign each required timeframe once. Missing: ${missingTimeframes.join(", ")}.`
                      : "Charts are mapped. Create the upload request before running analysis."}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {review ? (
        <section className={PANEL_CLASS}>
          <header className="border-b border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-700 dark:bg-slate-950/70">
            <h2 className="font-semibold text-slate-950 dark:text-white">
              Review result
            </h2>
          </header>
          <div className="space-y-2 p-5 text-sm text-slate-700 dark:text-slate-200">
            <p>
              Bias:{" "}
              <strong>
                {String(
                  review?.ai_raw_json?.action ||
                    review?.final_recommendation ||
                    "CONDITIONAL_BUY",
                ).toUpperCase()}
              </strong>{" "}
              · Confidence:{" "}
              {review?.ai_raw_json?.confidence ??
                review?.confidence_score ??
                "--"}{" "}
              · Entry:{" "}
              {review?.ai_raw_json?.entry ?? review?.entry_price ?? "--"} · SL:{" "}
              {review?.stop_loss ?? "--"} · TP:{" "}
              {[
                review?.take_profit_1,
                review?.take_profit_2,
                review?.take_profit_3,
              ]
                .filter((v) => v != null)
                .join(" / ") || "--"}{" "}
              · Readiness:{" "}
              <strong>
                {review?.ai_raw_json?.executionReadiness ||
                  review?.ai_raw_json?.execution_readiness?.reason ||
                  "--"}
              </strong>
            </p>
            <p>
              Plan:{" "}
              {review?.ai_raw_json?.reasoning?.summary ||
                review?.ai_raw_json?.trade_plan?.entry_logic ||
                "Conditional setup returned by deterministic engine."}
            </p>
            <p>Thesis: {review?.ai_raw_json?.summary?.thesis || "--"}</p>
            <p>
              Safe to forward:{" "}
              {review?.admin_review?.safeToForward
                ? "Yes"
                : `No (${review?.admin_review?.blockedReason || "blocked"})`}
            </p>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              <select
                className={CONTROL_CLASS}
                value={forwardMode}
                onChange={(e) => setForwardMode(e.target.value as any)}
              >
                <option value="all_live_users">
                  All active live-enabled users
                </option>
                <option value="one_user">One user</option>
                <option value="selected_users">Selected users</option>
              </select>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={proOnly}
                  onChange={(e) => setProOnly(e.target.checked)}
                />{" "}
                Platinum users only
              </label>
              {forwardMode === "one_user" ? (
                <input
                  className={CONTROL_CLASS}
                  value={targetUserId}
                  onChange={(e) => setTargetUserId(e.target.value)}
                  placeholder="User ID"
                />
              ) : null}
              {forwardMode === "selected_users" ? (
                <input
                  className={CONTROL_CLASS}
                  value={targetUserIds}
                  onChange={(e) => setTargetUserIds(e.target.value)}
                  placeholder="User IDs (comma-separated)"
                />
              ) : null}
            </div>
            <button
              className="mt-3 inline-flex min-h-10 items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 dark:disabled:bg-slate-700 dark:disabled:text-slate-400"
              disabled={
                !review?.admin_review?.safeToForward || forward.isPending
              }
              onClick={() => forward.mutate()}
            >
              <Send aria-hidden="true" className="h-4 w-4" />
              {forward.isPending ? "Forwarding..." : "Forward setup"}
            </button>
          </div>
        </section>
      ) : null}

      <section className={PANEL_CLASS}>
        <header className="border-b border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-700 dark:bg-slate-950/70">
          <h2 className="font-semibold text-slate-950 dark:text-white">
            Forwarded recommendations
          </h2>
        </header>
        <div className="divide-y divide-slate-200 dark:divide-slate-700">
          {(recommendations.data || []).map((row: any) => (
            <div
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 text-sm text-slate-700 dark:text-slate-200"
            >
              <span>
                #{row.id} <strong>{row.symbol}</strong>{" "}
                {String(row.direction || "").toUpperCase()} · pending{" "}
                {row.recipients_pending || 0} · confirmed{" "}
                {row.recipients_confirmed || 0} · rejected{" "}
                {row.recipients_rejected || 0} · expired{" "}
                {row.recipients_expired || 0}
              </span>
              <button
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                disabled={row.status !== "active" || cancel.isPending}
                onClick={() => cancel.mutate(row.id)}
              >
                <XCircle aria-hidden="true" className="h-4 w-4" /> Cancel
              </button>
            </div>
          ))}
          {!recommendations.isLoading &&
          !(recommendations.data || []).length ? (
            <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">
              No recommendations have been forwarded.
            </p>
          ) : null}
        </div>
      </section>

      <section className={PANEL_CLASS}>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <h2 className="font-semibold text-slate-950 dark:text-white">
            Recent admin analyses
          </h2>
          <div className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {analyses.isLoading
              ? "Loading..."
              : `${(analyses.data || []).length} analyses`}
          </div>
        </div>
      </section>
    </main>
  );
}
